'use client'
import React, { useEffect, useState } from 'react'
import RedeemVerificationEligible from '@/components/breakdown-popups/redeem-verification-eligible'
import RedeemAndVerificationFlow from '@/components/breakdown-popups/redeem-verification-flow'
import StripeRedeemAndVerificationFlow from '@/components/stripe/redeem-verification-flow'
import { useWalletBalanceQuery } from '@/src/queries/earnings/wallet-balance'
import { useUser } from '@/src/store/user-store'
import {
  Box,
  Button,
  Flex,
  Image,
  Skeleton,
  Text,
  useMediaQuery,
} from '@chakra-ui/react'
import { useSearchParams } from 'next/navigation'
import CombineMoreInfo from '../modals/combine-more-info'
import { formatLykCoinValue, formatLykCoins } from '@/lib/lyk-coin'

const RedeemableBalance = ({
  viewport,
}: {
  viewport: 'mobile' | 'desktop'
}) => {
  const [isDesktop] = useMediaQuery('(min-width: 48em)', {
    ssr: true,
    fallback: false,
  })
  const ownsVerificationFlow =
    viewport === 'desktop' ? isDesktop : !isDesktop
  const walletBalance = useWalletBalanceQuery()
  const { user, _hasHydrated } = useUser()
  const searchParams = useSearchParams()
  const lykBalance = parseFloat(
    walletBalance?.data?.verified?.lykCoin || '0'
  ).toFixed(1)
  const [launchFlowStripe, setLaunchFlowStripe] = useState(false)
  const notVerifyEligible =
    (user?.countryShortname === 'IN' && Number(lykBalance) < 130) ||
    (user?.countryShortname === 'US' && Number(lykBalance) < 1300) ||
    (user?.countryShortname === 'CA' && Number(lykBalance) < 1300) ||
    (user?.countryShortname === 'AE' && Number(lykBalance) < 1300) ||
    (user?.countryShortname === 'GB' && Number(lykBalance) < 1300) ||
    !user?.phoneNumber
      ? true
      : false
  const [isEligible, setIsEligible] = useState<boolean>(true)
  const [launchFlow, setLaunchFlow] = useState(false)
  const [isCombineMoreInfoOpen, setCombineMoreInfoOpen] = useState(false)
  const setIsEligibleFunc = () => {
    setIsEligible(false)
  }

  const today = new Date()

  const currentDay = today.getDate()

  const isAfterCycleEnd = currentDay > 21

  const payDayDate =
    new Date(
      today.getFullYear(),
      today.getMonth() + (isAfterCycleEnd ? 2 : 1),
      7
    ).toLocaleString('default', { month: 'short' }) + ' 7th'

  useEffect(() => {
    if (
      searchParams &&
      searchParams?.get('redeem') == 'true' &&
      _hasHydrated &&
      user?.countryShortname &&
      walletBalance?.data &&
      walletBalance?.data.hasOwnProperty('verified')
    ) {
      notVerifyEligible
        ? setIsEligibleFunc()
        : user?.countryShortname === 'IN'
          ? setLaunchFlow(true)
          : setLaunchFlowStripe(true)
    }
  }, [searchParams, notVerifyEligible, _hasHydrated, user, walletBalance.data])

  const handleCombineMoreInfoOpen = () => {
    setCombineMoreInfoOpen(true)
  }
  return (
    <>
      <CombineMoreInfo
        isOpen={isCombineMoreInfoOpen}
        onClose={() => setCombineMoreInfoOpen(false)}
      />
      {ownsVerificationFlow && (
        <RedeemAndVerificationFlow
          launchFlow={launchFlow}
          setLaunchFlow={setLaunchFlow}
          launchFlowType="redeem"
        />
      )}
      <Box>
        <RedeemVerificationEligible
          isOpen={!isEligible}
          onClose={() => setIsEligible(true)}
          proceedToNext={() => {
            setIsEligible(true)
            setLaunchFlow(true)
          }}
          type={'redeem'}
        />
      </Box>

      {!walletBalance.data?.unVerifiedRedeem.lykCoin &&
      !walletBalance.data?.unVerifiedRedeem.moneyValue ? (
        <></>
      ) : (
        <Box
          bg="linear-gradient(52.62deg, rgba(255, 255, 255, 0.2) 12.76%, rgba(36, 191, 163, 0.2) 110.86%)"
          boxShadow="0px 5px 9px -2px rgba(36, 191, 163, 0.40)"
          className="relative rounded-xl bg-cover bg-no-repeat p-3 text-white sm:p-5"
        >
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Text fontWeight={600} fontSize={[16, 16, 18]} color={'#727272'}>
              Your redeemable balance
            </Text>
            <Flex color={'lyk-green'} className="items-center font-semibold">
              More info{' '}
              <Image
                boxSize={5}
                src={'/rewards/breakdown/info-green.svg'}
                alt="Icon"
                marginLeft={'4px'}
                cursor={'pointer'}
                onClick={handleCombineMoreInfoOpen}
              />
            </Flex>
          </Flex>

          <hr className="my-4 font-semibold text-[#DDDDDD]" />

          {walletBalance.data?.verified.lykCoin &&
          ((user?.countryShortname === 'IN' &&
            parseFloat(walletBalance.data.verified.lykCoin) <= 0) ||
            (user?.countryShortname === 'US' &&
              parseFloat(walletBalance.data.verified.lykCoin) <= 0)) ? (
            <>
              <Flex className="my-2 w-full flex-col items-center justify-center gap-2">
                <Image
                  src={'/rewards/color-logo-bg.svg'}
                  alt="Icon"
                  w={{ base: 10, sm: 12 }}
                  h={{ base: 10, sm: 12 }}
                  mb={1}
                />

                <Text className="text-center text-sm font-medium text-[#727272] max-sm:mb-1">
                  You didn’t earn any LYK Coins in the last cycle.
                </Text>

                <Text
                  fontWeight={600}
                  color={'#727272'}
                  fontSize={18}
                  textAlign={'center'}
                  lineHeight={'normal'}
                >
                  Earnings from the current cycle will be redeemable on{' '}
                  <span className="text-[#24BFA3]">{payDayDate}</span>
                </Text>

                <Button
                  className="mt-1 w-full font-medium no-underline"
                  px={10}
                  isDisabled
                  bg={'#D9D9D9'}
                  _disabled={{
                    opacity: 1,
                    cursor: 'not-allowed',
                    bg: '#D9D9D9',
                    color: '#9F9F9F',
                  }}
                  color={'#9F9F9F'}
                  py={5}
                  _hover={'none'}
                  size={{ base: 'sm', sm: 'md' }}
                  textDecoration={'none'}
                >
                  Redeem Balance
                </Button>
              </Flex>
            </>
          ) : (
            <>
              <Flex
                mb={3}
                mt={1}
                alignItems="center"
                gap={2.5}
                color="lyk-green"
              >
                {walletBalance.isPending ? (
                  <Skeleton height="50px" my={3} width="100px"></Skeleton>
                ) : (
                  <>
                    <Flex
                      color="#3E3E3E"
                      className="w-full items-center justify-between gap-2"
                      fontWeight={600}
                    >
                      <Flex className="flex-col text-center text-[#797979]">
                        <Text fontSize={{ base: 20, sm: 24 }}>
                         {formatLykCoins(walletBalance.data?.verified.lykCoin) || '0'}
                        </Text>
                        <Flex
                          alignItems={'center'}
                          justifyContent={'center'}
                          gap={1}
                        >
                          <Image
                            boxSize={{ base: 4, sm: 7 }}
                            src="/logo-color-circle.svg"
                            alt="Lykstage"
                          />
                          <Text fontSize={{ base: 18, sm: 20 }}>LYK Coins</Text>
                        </Flex>
                      </Flex>

                      <Image
                        src="/rewards/compare.svg"
                        alt="compare"
                        boxSize={{ base: 4, sm: 5 }}
                      />

                      <Flex className="flex-col text-[#797979]">
                        <Text fontSize={{ base: 20, sm: 24 }}>
                         {formatLykCoinValue(walletBalance.data?.verified.lykCoin || '0.00')}
                        </Text>
                        <Flex justifyContent={'center'}>
                          <Text fontSize={{ base: 18, sm: 20 }}>US$</Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>
              <Flex gap={[2, 2, 5]} mt={[5, 5, 0]}>
                {!_hasHydrated ? (
                  <Skeleton width={'full'} height={'40px'} rounded={'md'} />
                ) : ((user?.countryShortname == 'US' || user?.countryShortname == 'CA' || user?.countryShortname == 'GB' || user?.countryShortname == 'AE') && !notVerifyEligible) ||
                  launchFlowStripe ? (
                  <StripeRedeemAndVerificationFlow
                    flowType="redeem"
                    openStripe={launchFlowStripe}
                  />
                ) : (
                  <Button
                    className="w-full no-underline"
                    px={10}
                    py={5}
                    bg="lyk-green"
                    color="white"
                    _hover={{ opacity: 0.7 }}
                    size={{ base: 'sm', sm: 'md' }}
                    fontWeight={500}
                    textDecoration={'none'}
                    onClick={() => {
                      notVerifyEligible
                        ? setIsEligibleFunc()
                        : setLaunchFlow(true)
                    }}
                  >
                    Redeem Balance
                  </Button>
                )}
              </Flex>
            </>
          )}
        </Box>
      )}
    </>
  )
}

export default RedeemableBalance
